import type { CustomFieldDefinition, CustomFieldEntity, CustomFieldType } from '@pm/schemas';
import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  Field,
  Input,
  LabelCaps,
  Segmented,
  Select,
  SkeletonRows,
  Textarea,
} from '@pm/ui';
import { ListPlus, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useCustomFields, useInvalidating } from '@/lib/queries';
import { useToast } from '@/lib/toast';

const TYPE_LABEL: Record<CustomFieldType, string> = {
  TEXT: 'Texto curto',
  TEXTAREA: 'Texto longo',
  NUMBER: 'Número',
  CURRENCY: 'Valor (R$)',
  DATE: 'Data',
  DATETIME: 'Data e hora',
  BOOLEAN: 'Sim/Não',
  SELECT: 'Seleção única',
  MULTISELECT: 'Seleção múltipla',
  USER: 'Usuário',
  CLIENT: 'Cliente',
  URL: 'URL',
  EMAIL: 'E-mail',
  PHONE: 'Telefone',
};

/** SPEC §38 / RULE-057 — definições de campos customizados por entidade. */
export function CustomFieldsSection() {
  const toast = useToast();
  const { can } = useAuth();
  const [entity, setEntity] = useState<CustomFieldEntity>('TASK');
  const { data = [], isLoading } = useCustomFields(entity);
  const [name, setName] = useState('');
  const [type, setType] = useState<CustomFieldType>('TEXT');
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState('');
  const [help, setHelp] = useState('');
  const [remove, setRemove] = useState<CustomFieldDefinition | null>(null);
  const inval = [['custom-fields'], ['custom-field-values']];
  const create = useInvalidating(
    (input: Record<string, unknown>) => api.post<CustomFieldDefinition>('/custom-fields', input),
    inval,
  );
  const update = useInvalidating(
    ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      api.patch<CustomFieldDefinition>(`/custom-fields/${id}`, input),
    inval,
  );
  const del = useInvalidating((id: string) => api.delete(`/custom-fields/${id}`), inval);
  const manage = can('settings.manage');
  const needsOptions = type === 'SELECT' || type === 'MULTISELECT';

  return (
    <div className="flex flex-col gap-gap">
      <Segmented
        ariaLabel="Entidade"
        value={entity}
        onChange={setEntity}
        options={[
          { value: 'TASK', label: 'Demandas' },
          { value: 'CLIENT', label: 'Clientes' },
        ]}
      />
      <div className="grid gap-gap-lg lg:grid-cols-[1fr_340px]">
        <div className="rounded-md border border-border bg-surface-1">
          {isLoading ? (
            <SkeletonRows rows={3} />
          ) : data.length === 0 ? (
            <EmptyState
              icon={ListPlus}
              title="Nenhum campo"
              description={`Campos aparecem na tela de ${entity === 'TASK' ? 'cada demanda' : 'cada cliente'}.`}
            />
          ) : (
            <ul>
              {data.map((d, i) => (
                <li
                  key={d.id}
                  className="flex items-center gap-3 border-b border-border px-4 py-2 last:border-b-0"
                >
                  <span className="w-5 font-mono text-xs text-text-3">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-sm">
                      <span className="font-medium">{d.name}</span>
                      <span className="font-mono text-xs text-text-3">{d.key}</span>
                      {d.required ? <Badge tone="warning">obrigatório</Badge> : null}
                    </p>
                    <p className="text-xs text-text-3">
                      {TYPE_LABEL[d.field_type]}
                      {d.options.length ? ` · ${d.options.join(', ')}` : ''}
                      {d.help_text ? ` · ${d.help_text}` : ''}
                    </p>
                  </div>
                  {manage ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          update.mutate(
                            { id: d.id, input: { required: !d.required } },
                            { onError: (e) => toast.error(e) },
                          )
                        }
                      >
                        {d.required ? 'Tornar opcional' : 'Tornar obrigatório'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Excluir"
                        onClick={() => setRemove(d)}
                      >
                        <Trash2 />
                      </Button>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
        {manage ? (
          <form
            className="flex flex-col gap-3 self-start rounded-md border border-border bg-surface-1 p-4"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate(
                {
                  entity_type: entity,
                  field_type: type,
                  name,
                  required,
                  options: needsOptions
                    ? options
                        .split('\n')
                        .map((o) => o.trim())
                        .filter(Boolean)
                    : [],
                  help_text: help || null,
                },
                {
                  onSuccess: () => (
                    setName(''),
                    setOptions(''),
                    setHelp(''),
                    setRequired(false),
                    toast.success('Campo criado')
                  ),
                  onError: (err) => toast.error(err),
                },
              );
            }}
          >
            <LabelCaps>Novo campo · {entity === 'TASK' ? 'demandas' : 'clientes'}</LabelCaps>
            <Field label="Nome" htmlFor="cf-name">
              <Input
                id="cf-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex.: Nº do contrato"
              />
            </Field>
            <Field label="Tipo" htmlFor="cf-type">
              <Select
                id="cf-type"
                value={type}
                onChange={(e) => setType(e.target.value as CustomFieldType)}
              >
                {(Object.keys(TYPE_LABEL) as CustomFieldType[]).map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
            </Field>
            {needsOptions ? (
              <Field label="Opções (uma por linha)" htmlFor="cf-opts">
                <Textarea
                  id="cf-opts"
                  value={options}
                  onChange={(e) => setOptions(e.target.value)}
                  className="min-h-[72px] font-mono text-xs"
                />
              </Field>
            ) : null}
            <Field label="Texto de ajuda" htmlFor="cf-help">
              <Input id="cf-help" value={help} onChange={(e) => setHelp(e.target.value)} />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={required} onCheckedChange={(v) => setRequired(v === true)} />{' '}
              Obrigatório
            </label>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={create.isPending}
              disabled={!name.trim() || (needsOptions && !options.trim())}
            >
              <Plus /> Criar campo
            </Button>
          </form>
        ) : null}
      </div>
      <ConfirmDialog
        open={remove !== null}
        onOpenChange={() => setRemove(null)}
        title={`Excluir campo ${remove?.name ?? ''}?`}
        description="Os valores já preenchidos deixam de aparecer."
        confirmLabel="Excluir"
        danger
        loading={del.isPending}
        onConfirm={() =>
          remove &&
          del.mutate(remove.id, {
            onSuccess: () => (toast.success('Campo excluído'), setRemove(null)),
            onError: (e) => toast.error(e),
          })
        }
      />
    </div>
  );
}
